/**
 * Brand tokens.
 *
 * Tone: a diplomatic dispatch. Dry, factual, with dates and numbers. Interface
 * copy reads like entries in an embassy log: what was done, with whom, how it
 * ended. No "AI-powered growth hacking".
 */

export const BRAND = {
  name: 'Legate',
  ticker: '$LEGATE',
  tagline: 'An envoy with authority',
  chain: 'Robinhood Chain',
  launchpad: 'pons.family',
} as const;

// ── Who wrote the turn ───────────────────────────────────────────────────────

/**
 * The name every tenant-facing surface uses for the agent.
 *
 * Which model answered is our supply chain, not the tenant's. An inbox footer,
 * a conversation export or an analytics breakdown carrying `anthropic/…` puts
 * our routing table into a workspace the tenant may screenshot for their own
 * client, and it tells them nothing they can act on.
 *
 * Operator surfaces are the exception and read the model id straight from the
 * row: the platform model page, the audit log, eval runs and cost attribution
 * exist to answer what a turn cost and which version wrote it, and a brand name
 * there answers neither.
 */
export const AGENT_NAME = 'Legate Agent';

/** The model id recorded when no model was called. Mirrors `OFFLINE_MODEL`. */
export const DETERMINISTIC_MODEL = 'offline/deterministic';

/**
 * The one distinction that survives the renaming.
 *
 * When the budget is spent or no key is configured, the deterministic engine
 * writes the turn and no model is called. The tenant is told, because they are
 * reading text produced by a different mechanism and hiding that would
 * misrepresent it.
 */
export const AGENT_OFFLINE_LABEL = `${AGENT_NAME} · offline`;

/** Stated plainly wherever the offline label appears. */
export const AGENT_OFFLINE_NOTE =
  'No model was called for this turn. The deterministic engine wrote it from the conversation state, the playbook variant and the approved knowledge base — the daily model budget was spent, or no model key is configured.';

/**
 * True for a turn the deterministic engine wrote.
 *
 * Accepts either a raw model id or a label already rendered from one, so a
 * value that has passed through `agentLabel` on the API side is still read
 * correctly in the browser.
 */
export function isDeterministicTurn(model: string | null): boolean {
  if (model === null) return false;
  const value = model.trim().toLowerCase();
  return value.startsWith('offline') || value === AGENT_OFFLINE_LABEL.toLowerCase();
}

/**
 * The only name a tenant surface may print for the author of a turn.
 *
 * `null` — no routing recorded — returns the plain name: the label states who
 * the tenant was dealing with, not how it was routed, and the offline suffix is
 * reserved for turns explicitly tagged as deterministic. Idempotent, so
 * applying it twice across the API and the client is harmless.
 */
export function agentLabel(model: string | null): string {
  return isDeterministicTurn(model) ? AGENT_OFFLINE_LABEL : AGENT_NAME;
}

/**
 * The two themes.
 *
 * Both are monochrome — a single neutral ramp — with one chromatic accent taken
 * from the brand mark (a blue stroke on a pale lavender ground) and two status
 * colours. The interface reads the same in either; only the direction of the
 * ramp changes.
 *
 * Contrast, measured against the theme's own `bg` (WCAG 2.1 relative luminance):
 *
 *              light                      dark
 *   text       17.58:1                    17.36:1
 *   muted .70   6.48:1                     8.69:1
 *   faint .45   2.90:1                     4.16:1   (de-emphasised metadata,
 *                                                    never body copy)
 *   accent      5.94:1                     5.28:1
 *   seal        7.91:1                    11.15:1
 *   alert       8.27:1                     7.03:1
 *
 * The dark accent is the logo blue lifted two steps: #0059E3 reaches only
 * 1.79:1 on #0D0D0D, #3B82F6 reaches 5.28:1 (and 4.87:1 on the dark surface).
 * Its hover lifts further rather than darkening, because darkening on a dark
 * ground is what breaks the ratio.
 */
export const THEME = {
  light: {
    bg: '#FFFFFF',
    surface: '#F4F3F3',
    surfaceHover: '#EAEAEA',
    text: '#191919',
    hairline: '#E5E5E5',
    accent: '#0059E3',
    accentHover: '#0049BD',
    seal: '#1F5C3D',
    alert: '#8C2F1E',
  },
  dark: {
    bg: '#0D0D0D',
    surface: '#171717',
    surfaceHover: '#1F1F1F',
    text: '#F2F2F2',
    hairline: '#2A2A2A',
    accent: '#3B82F6',
    accentHover: '#5B96F8',
    seal: '#4ADE80',
    alert: '#F87171',
  },
} as const;

export type ThemeName = keyof typeof THEME;

/** Opacity ramp applied to `text` for secondary and tertiary copy. */
export const TEXT_ALPHA = { muted: 0.7, faint: 0.45 } as const;

/**
 * Flat aliases at their light-theme values.
 *
 * Kept for surfaces that cannot follow a theme: the OAuth pages served by the
 * Meta simulator are standalone documents with no token layer, and the browser
 * chrome colour has to be a literal hex.
 */
export const COLORS = {
  ink: THEME.light.text,
  paper: THEME.light.bg,
  panel: THEME.light.surface,
  panelHover: THEME.light.surfaceHover,
  hairline: THEME.light.hairline,
  /** Interactive affordance only: focus, active rule, links, selected tabs. */
  accent: THEME.light.accent,
  accentHover: THEME.light.accentHover,
  /** Reserved for policy blocks and suspensions only. */
  alert: THEME.light.alert,
  /** Reserved for confirmed/qualified states only. */
  seal: THEME.light.seal,
} as const;

/**
 * The mark. Three strokes, viewBox 0 0 256 256, fill="currentColor".
 * Rendered at w-6 h-6 in the navbar beside the wordmark.
 */
export const LOGO_PATHS = [
  'M 144 256 L 27.598 256 L 144 139.598 Z',
  'M 256 207.5 L 200 256 L 200 56 L 0 56 L 48 0 L 256 0 Z',
  'M 0 204.402 L 0 112 L 92.402 112 Z',
] as const;

/** Single-path form, for favicons and anywhere one `d` attribute is needed. */
export const LOGO_PATH = LOGO_PATHS.join(' ');
